import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { Faq } from "@/components/Faq";
import { AdSlot } from "@/components/AdSlot";
import { STANDARD_RECT, STANDARD_ROUND } from "@/lib/teppich";
import { TABELLE_SEITE, TEPPICH_SEITEN } from "@/lib/teppichSeiten";

const VERWENDUNG = {
  "60×120": "Flur, Bett-Läufer",
  "80×150": "Eingang, kleiner Bereich",
  "120×170": "kleines Wohnzimmer, vor dem Sofa",
  "140×200": "Wohnzimmer, vor dem Sofa",
  "160×230": "Wohnzimmer, kleiner Esstisch",
  "170×240": "beliebte Wohnzimmergröße",
  "200×200": "quadratisch, runder Esstisch",
  "200×290": "Sofa, Esstisch, Schlafzimmer",
  "240×340": "großer Esstisch, große Sitzgruppe",
  "300×400": "großes Wohnzimmer",
};

const FAQ = TABELLE_SEITE.faq;

export default function TeppichTabellePage() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const canonical = origin ? `${origin}/${TABELLE_SEITE.slug}` : undefined;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <Seo title={TABELLE_SEITE.title} description={TABELLE_SEITE.description} canonical={canonical} />
      <JsonLd data={jsonLd} />

      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{TABELLE_SEITE.h1}</h1>
      <p className="mt-3 text-muted-foreground leading-relaxed">
        Teppiche gibt es in festen Standardgrößen. Diese Übersicht zeigt die gängigen Maße in cm und wofür sie typischerweise passen. Welche Größe in deinem Fall passt, rechnet dir der{" "}
        <Link to="/teppichgroesse" className="text-primary hover:underline">Teppichgrößen-Rechner</Link> aus.
      </p>

      <section className="mt-6">
        <h2 className="text-xl font-semibold mb-3">Rechteckige Teppiche</h2>
        <div className="overflow-hidden rounded-xl border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Größe (cm)</th>
                <th className="px-4 py-2 font-medium">Typische Verwendung</th>
              </tr>
            </thead>
            <tbody>
              {STANDARD_RECT.map(([s, l]) => {
                const key = `${s}×${l}`;
                return (
                  <tr key={key} className="border-t">
                    <td className="px-4 py-2 font-medium">{s} × {l}</td>
                    <td className="px-4 py-2 text-muted-foreground">{VERWENDUNG[key] || ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-6">
        <h2 className="text-xl font-semibold mb-3">Runde Teppiche</h2>
        <p className="text-muted-foreground">
          Gängige Durchmesser: {STANDARD_ROUND.map((d) => `Ø ${d} cm`).join(", ")}.
        </p>
      </section>

      <div className="mt-8"><AdSlot variant="banner" /></div>

      <section className="mt-8">
        <h2 className="text-xl font-semibold mb-3">Passende Größe für deinen Fall</h2>
        <ul className="grid sm:grid-cols-2 gap-2">
          {TEPPICH_SEITEN.map((s) => (
            <li key={s.slug}>
              <Link to={`/${s.slug}`} className="text-primary hover:underline">{s.h1}</Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="text-xl font-semibold mb-4">Häufige Fragen</h2>
        <Faq items={FAQ} testId="tabelle-faq" />
      </section>

      <div className="mt-10"><AdSlot variant="banner" /></div>
    </div>
  );
}
